import appConfig from '../../configs/appConfig.js';
import envVariables from '../../consts/envVariables';
import * as uamMethods from '../../consts/uam/uamMethods';
import {selectUserLocationId} from '../../state/selectors/auth';
import {selectIsIncludeEmptyPuff} from '../../state/selectors/session.js';
import {getState} from '../../state/store.js';
import damMappingService from '../dam/damMappingService';
import retailMappingService from '../retail/retailMappingService';
import uamClient from './uamClient';
import uamMappingService from './uamMappingService';

const getUserDevices = () =>
    uamClient.callGet({
        path: uamMethods.USER_DEVICES,
        mapping: damMappingService.userDevicesMapping,
        isThrowError: true,
        isOfflineResponse: true,
    });

const postAuthToken = async (serialNumber, assetId, consents) => {
    const consentFlags = Object.keys(consents).filter((key) => consents[key]);

    const body = {
        externalId: envVariables.APP_TYPE,
        userType: envVariables.APP_TYPE,
        appType: envVariables.APP_TYPE,
        market: appConfig.getMarket(),
        ageVerified: true,
        consentFlags,
        assets: [
            {
                id: serialNumber,
                idType: 'SERIAL',
            },
        ],
    };

    const queryParams = {
        project: envVariables.INC_CLOUD_PROJECT,
        assetId, // Unique per-device query param to avoid reusing cached response for same URL
    };

    return await uamClient.callPost({
        endpoint: envVariables.INC_SHARED_API_ENDPOINT,
        path: uamMethods.AUTH_TOKEN,
        body,
        mapping: damMappingService.authTokenMapping,
        isThrowError: true,
        isOfflineResponse: true,
        queryParams,
    });
};

const getSubscription = (serialNumber) =>
    uamClient.callGet({
        path: uamMethods.SUBSCRIPTION,
        mapping: uamMappingService.subscriptionMapping,
        serialNumber,
        isUamToken: true,
        isThrowError: true,
    });

const getDeviceInfo = (serialNumber, isThrowError, isCached) =>
    uamClient.callGet({
        path: uamMethods.DEVICE_INFO,
        mapping: uamMappingService.deviceInfoMapping,
        serialNumber,
        isCached,
        isThrowError,
        isUamToken: true,
        isOfflineResponse: true,
    });

const postUserDevice = async ({dusn, codentify}) => {
    const body = {};

    if (dusn) {
        body.dusn = dusn;
    }

    if (codentify) {
        body.codentify = codentify;
    }

    return await uamClient.callPost({
        path: uamMethods.USER_DEVICES,
        mapping: uamMappingService.registrationMapping,
        body,
        isThrowError: true,
    });
};

const delUserDevice = async (assetId) => {
    const queryParams = {
        asset_id: assetId,
    };

    await uamClient.callDel({
        path: uamMethods.USER_DEVICES,
        queryParams,
        isThrowError: true,
    });
};

const getDeviceConnectionStatus = (serialNumber, isThrowError) =>
    uamClient.callGet({
        path: uamMethods.DEVICE_CONNECTION_STATUS,
        mapping: uamMappingService.deviceConnectionStatusMapping,
        serialNumber,
        isUamToken: true,
        isThrowError,
    });

const postDeviceSynchronize = (serialNumber) =>
    uamClient.callPost({
        path: uamMethods.DEVICE_SYNCHRONIZE,
        mapping: damMappingService.deviceSynchronisationMapping,
        serialNumber,
        isUamToken: true,
        isThrowError: true,
    });

const getDevicePairing = async (codentify) => {
    const queryParams = {codentify};

    return await uamClient.callGet({
        path: uamMethods.DEVICE_PAIRING,
        isThrowError: true,
        queryParams,
    });
};

const postDevicePairing = async (codentify) => {
    const body = {
        codentify,
    };

    return await uamClient.callPost({
        path: uamMethods.DEVICE_PAIRING,
        body,
        isThrowError: true,
    });
};

const getFirmwareUpdateAvailability = (serialNumber, language, isCached) => {
    const queryParams = {
        language,
        releaseNotes: true,
    };

    return uamClient.callGet({
        path: uamMethods.GET_AVAILABLE_FIRMWARE,
        serialNumber,
        isCached,
        isThrowError: true,
        isUamToken: true,
        isOfflineResponse: true,
        mapping: damMappingService.firmwareAvailabilityMapping,
        queryParams,
    });
};

const postUpdateFirmware = (serialNumber) => {
    return uamClient.callPost({
        path: uamMethods.UPDATE_FIRMWARE,
        serialNumber,
        isThrowError: true,
        isUamToken: true,
        mapping: damMappingService.firmwareUpdateMapping,
    });
};

const postUpdateSetting = (serialNumber, settingName, newValue) => {
    const queryParams = {
        settingName,
    };

    const body = {
        settingName,
        newValue,
    };

    return uamClient.callPost({
        path: uamMethods.UPDATE_DEVICE_SETTING,
        serialNumber,
        isThrowError: true,
        isUamToken: true,
        isOnlineRequest: true,
        body,
        queryParams,
    });
};

const getUserConsents = async () => {
    return uamClient.callGet({
        path: uamMethods.USER_CONSENTS,
        mapping: uamMappingService.userConsentsMapping,
        isOfflineResponse: true,
        isThrowError: true,
    });
};

const putUserConsents = async (consents, isOnlineRequest) => {
    const body = {
        consents,
    };

    return uamClient.callPut({
        path: uamMethods.USER_CONSENTS,
        isThrowError: true,
        body,
        isOnlineRequest,
    });
};

const getRequestStatus = (serialNumber, requestId) => {
    return uamClient.callGet({
        path: uamMethods.DEVICE_REQUEST_STATUS,
        mapping: uamMappingService.requestStatusMapping,
        serialNumber,
        isUamToken: true,
        pathParams: [requestId],
    });
};

const postUserSubscriptionInfo = async (subscription, languageCode, notificationTypes) => {
    const body = {
        subscription,
        localization: languageCode,
        notification_types: notificationTypes,
        markets: [appConfig.getMarket()],
        app_types: [envVariables.APP_TYPE],
    };

    return uamClient.callPost({
        endpoint: envVariables.INC_SHARED_API_ENDPOINT,
        path: uamMethods.USER_SUBSCRIPTION_INFO,
        isThrowError: true,
        isOnlineRequest: true,
        body,
    });
};

const putUserSubscriptionInfo = async (endpoint, languageCode, notificationTypes) => {
    const body = {
        endpoint,
        localization: languageCode,
        notification_types: notificationTypes,
        markets: [appConfig.getMarket()],
        app_types: [envVariables.APP_TYPE],
    };

    return uamClient.callPut({
        endpoint: envVariables.INC_SHARED_API_ENDPOINT,
        path: uamMethods.USER_SUBSCRIPTION_INFO,
        isThrowError: true,
        body,
    });
};

const delUserSubscriptionInfo = async (endpoint) => {
    const queryParams = {
        endpoint,
    };

    return uamClient.callDel({
        endpoint: envVariables.INC_SHARED_API_ENDPOINT,
        path: uamMethods.USER_SUBSCRIPTION_INFO,
        isThrowError: true,
        queryParams,
    });
};

const postUserCampaignTracker = async (campaignName, notificationType, modelType) => {
    const body = {
        campaign_name: campaignName,
        notification_type: notificationType,
        device_types: modelType ? [modelType] : [],
        markets: [appConfig.getMarket()],
        app_types: [envVariables.APP_TYPE],
    };

    return uamClient.callPost({
        endpoint: envVariables.INC_SHARED_API_ENDPOINT,
        path: uamMethods.USER_CAMPAIGN_TRACKER,
        body,
    });
};

const getUserMessages = async (props, signal) => {
    const config = {
        endpoint: envVariables.INC_SHARED_API_ENDPOINT,
        path: uamMethods.MESSAGES,
        mapping: uamMappingService.userMessagesMapping,
        isThrowError: true,
        signal,
    };

    if (props?.queryParams) {
        config.queryParams = props.queryParams;
    }

    return uamClient.callGet(config);
};

const putUpdateUserMessage = async ({messageId, isRead, isDeleted}) => {
    const config = {
        endpoint: envVariables.INC_SHARED_API_ENDPOINT,
        path: uamMethods.MESSAGES,
        mapping: uamMappingService.userMessagesMapping,
        body: {
            messageId,
            isRead,
            isDeleted,
        },
    };

    return uamClient.callPut(config);
};

const postGenerateExperienceInference = async (serialNumber) => {
    return uamClient.callPost({
        path: uamMethods.EXPERIENCE_INFERENCE_GENERATE,
        serialNumber,
        isUamToken: true,
        isThrowError: true,
    });
};

const getExperienceInference = async (serialNumber) => {
    return uamClient.callGet({
        path: uamMethods.EXPERIENCE_INFERENCE_GET,
        serialNumber,
        isThrowError: true,
        isUamToken: true,
        isOfflineResponse: true,
        mapping: uamMappingService.experienceInferenceMapping,
    });
};

const getDeviceExperienceData = (serialNumber, queryParams, key) =>
    uamClient.callGet({
        path: uamMethods.DEVICE_EXPERIENCE_DATA,
        serialNumber,
        isThrowError: true,
        isUamToken: true,
        isOfflineResponse: true,
        mapping: uamMappingService.experienceDataMapping,
        queryParams: {
            includeEmptyPuff: selectIsIncludeEmptyPuff(getState()),
            ...queryParams,
        },
        pathParams: [key],
    });

const getUserBadges = async ({isCached, isThrowError, isWaitingCache, user_id, user_ids}) => {
    return uamClient.callGet({
        path: uamMethods.USER_BADGES,
        isThrowError,
        isOfflineResponse: true,
        isWaitingCache,
        isCached,
        mapping: uamMappingService.userBadgesMapping,
        queryParams: {
            ...(user_id && {user_id}),
            ...(user_ids && {user_ids}),
        },
    });
};

const postUserBadges = async (body) => {
    return uamClient.callPost({
        path: uamMethods.USER_BADGES,
        isThrowError: true,
        isOnlineRequest: true,
        body,
    });
};

const getUserConversionStatus = async ({isCached, isThrowError}) => {
    return uamClient.callGet({
        path: uamMethods.USER_CONVERSION_STATUS,
        mapping: uamMappingService.userConversionStatusMapping,
        isThrowError,
        isCached,
        isOfflineResponse: true,
    });
};

const postConversionData = async (body) => {
    return uamClient.callPost({
        path: uamMethods.USER_SURVEY,
        isThrowError: true,
        isOfflineResponse: true,
        body,
    });
};

const getSilentFwuConfig = async (serialNumber) => {
    return uamClient.callGet({
        path: uamMethods.SILENT_FWU_CONFIG,
        serialNumber,
        isThrowError: true,
        isUamToken: true,
        isOfflineResponse: true,
        mapping: uamMappingService.silentFwuConfigMapping,
    });
};

const postSilentFwuConfig = async (serialNumber, body) => {
    return uamClient.callPost({
        path: uamMethods.SILENT_FWU_CONFIG,
        serialNumber,
        isThrowError: true,
        isUamToken: true,
        body,
    });
};

const postConversionFeedback = async ({overallFeedback, highlights}) => {
    const body = {
        overallFeedback: +overallFeedback,
        highlights,
    };

    return uamClient.callPost({
        path: uamMethods.USER_CONVERSION_FEEDBACK,
        isImportantRequest: true,
        isOnlineRequest: true,
        isThrowError: true,
        body,
    });
};

const postAppFeedback = async ({score, feedback}) => {
    const body = {
        score: +score,
        feedback,
    };

    return uamClient.callPost({
        path: uamMethods.USER_APP_FEEDBACK,
        isImportantRequest: true,
        isOnlineRequest: true,
        isThrowError: true,
        body,
    });
};

// RETAIL
const getRetailDevices = async (isCached, codentify) => {
    const locationId = selectUserLocationId(getState());

    return uamClient.callGet({
        path: uamMethods.RETAIL_DEVICE,
        mapping: uamMappingService.retailDevicesMapping,
        isOfflineResponse: true,
        isCached,
        queryParams: {
            ...(locationId && !codentify && {locationId}),
            ...(codentify && {codentify}),
        },
    });
};

const postRetailAuthToken = async (codentify, consents) => {
    const consentFlags = Object.keys(consents).filter((key) => consents[key]);

    const queryParams = {
        project: envVariables.INC_CLOUD_PROJECT,
    };

    const body = {
        externalId: envVariables.APP_TYPE,
        userType: envVariables.APP_TYPE,
        appType: envVariables.APP_TYPE,
        market: appConfig.getMarket(),
        ageVerified: true,
        consentFlags,
        assets: [
            {
                id: codentify,
                idType: 'CODENTIFY',
            },
        ],
    };

    return await uamClient.callPost({
        endpoint: envVariables.INC_SHARED_API_ENDPOINT,
        path: uamMethods.RETAIL_GET_AUTH_TOKEN,
        body,
        mapping: retailMappingService.authTokenMapping,
        isThrowError: true,
        queryParams,
    });
};

const getRetailRewards = async ({user_id, user_ids, isCached}) => {
    return uamClient.callGet({
        path: uamMethods.RETAIL_REWARDS,
        mapping: uamMappingService.retailRewardsMapping,
        isThrowError: !isCached,
        isOfflineResponse: true,
        isCached,
        queryParams: {
            ...(user_id && {user_id}),
            ...(user_ids && {user_ids}),
        },
    });
};

const postRetailRewards = async (user_id, badge_levels) => {
    return uamClient.callPost({
        path: uamMethods.RETAIL_REWARDS,
        isThrowError: true,
        body: {
            user_id,
            badge_levels,
        },
    });
};

const postRetailUserVisit = async (user_id) => {
    const dateNow = new Date(Date.now());

    const date =
        `${String(dateNow.getUTCDate()).padStart(2, '0')}/` +
        `${String(dateNow.getUTCMonth() + 1).padStart(2, '0')}/` +
        `${dateNow.getUTCFullYear()} ` +
        `${String(dateNow.getUTCHours()).padStart(2, '0')}:` +
        `${String(dateNow.getUTCMinutes()).padStart(2, '0')}`;

    return uamClient.callPost({
        path: uamMethods.RETAIL_USER_VISIT,
        isThrowError: true,
        body: {
            user_id,
            date,
        },
    });
};

export default {
    getUserDevices,
    postAuthToken,
    getSubscription,
    getDeviceInfo,
    postUserDevice,
    delUserDevice,
    getDeviceConnectionStatus,
    postDeviceSynchronize,
    getDevicePairing,
    postDevicePairing,
    getFirmwareUpdateAvailability,
    postUpdateFirmware,
    postUpdateSetting,
    getUserConsents,
    putUserConsents,
    getRequestStatus,
    postUserSubscriptionInfo,
    putUserSubscriptionInfo,
    delUserSubscriptionInfo,
    postUserCampaignTracker,
    getUserMessages,
    putUpdateUserMessage,
    postGenerateExperienceInference,
    getExperienceInference,
    getDeviceExperienceData,
    getUserBadges,
    postUserBadges,
    postConversionData,
    getUserConversionStatus,
    getSilentFwuConfig,
    postSilentFwuConfig,
    postConversionFeedback,
    postAppFeedback,
    getRetailDevices,
    getRetailRewards,
    postRetailRewards,
    postRetailUserVisit,
    postRetailAuthToken,
};
